/**
 * Cassanova API Client - Public Interface
 *
 * This module provides the public API facade for the Cassanova API client.
 * It exports only the endpoint functions, hiding implementation details.
 *
 * @module cassanova
 */

import {getAllProducts, getCategories, getProductById, getProducts,} from './endpoints';

import {ErrorCodes, getErrorMessage, isAuthError, isNetworkError, isRetryableError,} from './apiClient';

import tokenManager from './tokenManager';

/**
 * Menu API - Public interface for menu data operations
 *
 * @namespace menuApi
 */
export const menuApi = {
    /**
     * Get all menu categories
     * @function
     * @memberof menuApi
     * @returns {Promise<Object>} Categories response with categories array and totalCount
     */
    getCategories,

    /**
     * Get products with optional category filtering
     * @function
     * @memberof menuApi
     * @param {string|null} categoryId - Optional category ID to filter products
     * @param {number} start - Starting index for pagination (default: 0)
     * @param {number} limit - Maximum number of products to return (default: 50)
     * @returns {Promise<Object>} Products response with products array and totalCount
     */
    getProducts,

    /**
     * Get all products from all categories
     * @function
     * @memberof menuApi
     * @param {number} pageSize - Number of products to fetch per request (default: 100)
     * @returns {Promise<Object>} Response with all products array and totalCount
     */
    getAllProducts,

    /**
     * Get a single product by ID
     * @function
     * @memberof menuApi
     * @param {string} productId - Product ID to fetch
     * @returns {Promise<Object|null>} Product object or null if not found
     */
    getProductById,
};

/**
 * Error utilities for handling API errors
 *
 * @namespace errorUtils
 */
export const errorUtils = {
    /**
     * Error code constants
     * @memberof errorUtils
     */
    ErrorCodes,

    /**
     * Check if an error is retryable
     * @function
     * @memberof errorUtils
     * @param {Object} error - Categorized error object
     * @returns {boolean} True if error is retryable
     */
    isRetryableError,

    /**
     * Extract user-friendly error message
     * @function
     * @memberof errorUtils
     * @param {Object} error - Categorized error object
     * @returns {string} User-friendly error message
     */
    getErrorMessage,

    /**
     * Check if error is authentication-related
     * @function
     * @memberof errorUtils
     * @param {Object} error - Categorized error object
     * @returns {boolean} True if error is auth-related
     */
    isAuthError,

    /**
     * Check if error is network-related
     * @function
     * @memberof errorUtils
     * @param {Object} error - Categorized error object
     * @returns {boolean} True if error is network-related
     */
    isNetworkError,
};

/**
 * Token management utilities (for advanced use cases)
 *
 * @namespace tokenUtils
 */
export const tokenUtils = {
    /**
     * Get current token information
     * @function
     * @memberof tokenUtils
     * @returns {Object|null} Token info with expiry details or null
     */
    getTokenInfo: () => tokenManager.getTokenInfo(),

    /**
     * Force refresh the authentication token
     * @function
     * @memberof tokenUtils
     * @returns {Promise<string>} New access token
     */
    refreshToken: () => tokenManager.refreshToken(),

    /**
     * Clear cached token
     * @function
     * @memberof tokenUtils
     */
    clearToken: () => tokenManager.clearToken(),
};

/**
 * Default export - Menu API
 */
export default menuApi;

/**
 * Named exports for convenience
 */
export {
    getCategories,
    getProducts,
    getAllProducts,
    getProductById,
    ErrorCodes,
    isRetryableError,
    getErrorMessage,
    isAuthError,
    isNetworkError,
};
